import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { Button, Typography, Card, Input, Chip } from "@material-tailwind/react";
import { Search, Sort, Download } from "iconoir-react";

import { getRatingColor } from "../../../../utils/formatViews";
import Loader from "../../../../components/Loader";
import { RootState } from "../../../../redux";
import { getInspectionsRequest, InspectionSection, updateVehicleRequest, Vehicle } from "../../../../redux/vehicles/actions";
import { getDateTimeFormat, getTextFormat } from "../../../../utils/formatViews";
import { TabInspectionCard } from "./TabInspectionCard";

const TABLE_HEAD = [
    {n: "Inspection Type", className: ""},
    {n: "Inspection Date", className: ""},
    {n: "Employee", className: ""},
    {n: "Result", className: ""},
];

export function DashboardVehicleInventoryTabInspection() {
    const dispatch = useDispatch();
    const { vehicle, inspections } = useSelector((state: RootState) => state.vehicles);
    const { auth } = useSelector((state: RootState) => state.auth);
    const [ selectedInspection, setSelectedInspection ] = useState<InspectionSection | null>(null);

    useEffect(() => {
        dispatch(getInspectionsRequest());
    }, [dispatch]);

    const calculateScore = (answers: any) => {
        let inspection_score: { [key: number]: number } = {};
        let inspection_points: { [key: number]: number } = {};

        selectedInspection?.inspections.forEach((section) => {
            inspection_score[section.id] = section.questions.map((question) => {
                let opt_id = answers[question.id];
                let opt = question.options.filter((option) => option.id === opt_id)[0];
                return opt?.score || 0;
            }).reduce((accumulator, a) => accumulator + a, 0);

            inspection_points[section.id] = section.questions.map((question) => {
                let opt_id = answers[question.id];
                let opt = question.options.filter((option) => option.id === opt_id)[0];

                if (opt?.score == null)
                    return 0;
                else {
                    return question.options.reduce((max, option) => {
                        return (option.score ?? 0) > max ? (option.score ?? 0) : max;
                    }, 0);
                }
            }).reduce((accumulator, a) => accumulator + a, 0);
        });

        const total_points = selectedInspection?.inspections?.reduce((accumulator, section) => {
            return accumulator + inspection_points[section.id];
        }, 0);

        return (selectedInspection?.inspections?.map((section) => {
            return (inspection_score?.[section.id] ?? 0) / (inspection_points?.[section.id] || 1) * (inspection_points?.[section.id] ?? 1) / (total_points || 1);
        }).reduce((accumulator, a) => accumulator + a, 0) ?? 0) * 5;
    }

    return !vehicle ? (
        <Loader />
    ) : (
        <React.Fragment>
            {selectedInspection ? (
                <TabInspectionCard
                    section={selectedInspection}
                    vehicle={vehicle}
                    onClose={() => {
                        setSelectedInspection(null);
                        console.log("close");
                    }}
                    onSave={(answers) => {
                        const inspection_score = calculateScore(answers);
                        const employee =
                            auth?.user?.user?.first_name && auth?.user?.user?.last_name
                                ? auth?.user?.user?.first_name + " " + auth?.user?.user?.last_name
                                : auth?.user?.user?.first_name || auth?.user?.user?.last_name || auth?.user?.user?.email;

                        dispatch(
                            updateVehicleRequest({
                                vehicle: {
                                    id: vehicle.id,
                                    inspection_results: {
                                        employee: {
                                            ...vehicle.inspection_results?.["employee"],
                                            [selectedInspection.id]: employee,
                                        },
                                        date: {
                                            ...vehicle.inspection_results?.["date"],
                                            [selectedInspection.id]: new Date(),
                                        },
                                        inspection_score: {
                                            ...vehicle.inspection_results?.["inspection_score"],
                                            [selectedInspection.id]: inspection_score,
                                        },
                                        sections: {
                                            ...vehicle.inspection_results?.["sections"],
                                            [selectedInspection.id]: answers,
                                        },
                                    },
                                } as unknown as Vehicle,
                                update_fields: ["id", "inspection_results"],
                            })
                        );

                        setSelectedInspection(null);
                        console.log("save", answers);
                    }}
                />
            ) : (
                <Card className="p-5">
                    <Card.Header className="w-full m-0 items-start rounded-none flex flex-wrap gap-4 justify-between p-4">
                        <div>
                            <Typography type="lead" color="default" className="font-semibold mb-1">
                                List of Inspections
                            </Typography>
                            <Typography type="small" className="text-foreground block">
                                A summary of inspections with dates, locations, and outcomes.
                            </Typography>
                        </div>
                        <div className="flex flex-col sm:flex-row items-center w-full shrink-0 gap-3 md:w-max">
                            <Input placeholder="Search here..." className="w-full sm:w-72">
                                <Input.Icon>
                                    <Search className="w-full h-full" />
                                </Input.Icon>
                            </Input>
                            <div className="flex gap-3 sm:w-max w-full">
                                <Button
                                    variant="outline"
                                    color="secondary"
                                    className="flex items-center gap-2 sm:w-auto w-full"
                                >
                                    Filter
                                    <Sort className="w-4 h-4" />
                                </Button>
                                <Button
                                    variant="outline"
                                    color="secondary"
                                    className="flex items-center gap-2 sm:w-auto w-full"
                                >
                                    Export
                                    <Download className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </Card.Header>
                    <Card.Body className="mt-4 p-0 rounded-none max-h-[calc(100vh-340px)] overflow-y-auto scrollbar-hide">
                        <table className="w-full min-w-max table-auto">
                            <thead>
                                <tr>
                                    {TABLE_HEAD.map((el, idx: number) => (
                                        <th
                                            key={idx}
                                            className={`sticky top-0 bg-white z-10 border-b text-start border-surface p-4 ${el.className}`}
                                        >
                                            <Typography
                                                type="small"
                                                color="default"
                                                className="block font-medium"
                                            >
                                                {el.n}
                                            </Typography>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {inspections.map((el, idx: number) => (
                                    <tr key={idx} className="h-20 cursor-pointer" onClick={() => setSelectedInspection(el)}>
                                        <td className="p-4 border-b border-surface">
                                            <Typography color="primary" className="block font-bold">
                                                {el.title}
                                            </Typography>
                                        </td>
                                        <td className="p-4 border-b border-surface">
                                            <Typography
                                                type="small"
                                                className="block text-foreground"
                                            >
                                                {getDateTimeFormat(
                                                    vehicle.inspection_results?.["date"]?.[el.id]
                                                )}
                                            </Typography>
                                        </td>
                                        <td className="p-4 border-b border-surface">
                                            <Typography
                                                type="small"
                                                className="block text-foreground"
                                            >
                                                {getTextFormat(
                                                    vehicle.inspection_results?.["employee"]?.[
                                                        el.id
                                                    ]
                                                )}
                                            </Typography>
                                        </td>
                                        <td className="p-4 border-b border-surface">
                                            {vehicle.inspection_results?.['inspection_score']?.[el.id] ? (
                                                <Chip
                                                    size="sm"
                                                    isPill={false}
                                                className="w-max"
                                                color={getRatingColor(vehicle.inspection_results?.['inspection_score']?.[el.id])}
                                            >
                                                <Chip.Label className="capitalize">
                                                    {vehicle.inspection_results?.['inspection_score']?.[el.id].toFixed(2)}
                                                </Chip.Label>
                                                </Chip>
                                            ) : (
                                                <Button
                                                    variant="outline"
                                                    color="secondary"
                                                    className="flex items-center gap-2 sm:w-auto w-full"
                                                >
                                                    Start Inspection
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card.Body>
                </Card>
            )}
        </React.Fragment>
    );
}
